import { z } from "zod";
import { WORK_CLASSES } from "@excellent/core/dod";
import type {
  DodLabelResultView,
  DodSubjectView,
  TaskDodView,
} from "@excellent/core/dod";
import type { Operation } from "../types";

// Definition of Done — the task peek's DoD panel (#44086 part 2,
// RATCHET-UNIFICATION-PRD §3).
//
// THE PREMISE THIS SERVES: telemetry that lives only in a run dir or a log file
// cannot reach a user. `dod_criteria`, `gate_run_receipts` and the `dod:*` rows
// in `excellent_ops_events` are the ratchet program's whole memory, and until
// now they were readable only by SQL and the CLI. The DB is the system of
// record; this contract is the read of it.
//
// EVERY FIELD HERE IS RECORDED STATE, NEVER RECOMPUTED. The shell does not
// re-run the classifier (it lives in the plain-Node MCP) and does not re-run a
// gate. It reads what the deriver and the router already wrote — which is the
// only way the peek and the `done` gate can be guaranteed to agree.

/** The nine names the classifier can produce — sourced from the classifier's
 *  own vocabulary (@excellent/core/dod), never a second hand-kept list. */
export const workClassSchema = z.enum(
  WORK_CLASSES as unknown as [string, ...string[]],
);

/** A gate receipt backing one criterion — the proof, not a claim about it. */
export const dodReceiptSchema = z.object({
  command: z.string(),
  exitCode: z.number(),
  treeHash: z.string(),
  /** 'staged' (a clean tree) | 'dirty' — a dirty receipt proves less. */
  binding: z.string(),
  /** null | 'delta' — a delta receipt attests "no NEW violations". */
  mode: z.string().nullable(),
  baseCount: z.number().nullable(),
  headCount: z.number().nullable(),
  ranAt: z.string(),
});
export type DodReceipt = z.infer<typeof dodReceiptSchema>;

export const dodCriterionSchema = z.object({
  uid: z.string().nullable(),
  id: z.number(),
  kind: z.string(),
  ref: z.string().nullable(),
  threshold: z.number().nullable(),
  source: z.string(),
  why: z.string().nullable(),
  /** Does it GATE `done`? (isBindingCriterion — bound_at set, or authored.) */
  binding: z.boolean(),
  /** Did a human/mint CONFIRM it? A criterion can be binding and unconfirmed. */
  bound: z.boolean(),
  boundAt: z.string().nullable(),
  /** Latest recorded verdict from `dod:evaluated`; null ⇒ never evaluated. */
  pass: z.boolean().nullable(),
  proof: z.string().nullable(),
  attempt: z.number().nullable(),
  evaluatedAt: z.string().nullable(),
  receipt: dodReceiptSchema.nullable(),
});
export type DodCriterion = z.infer<typeof dodCriterionSchema>;

/** One `dod:retried` row — what changed between two evaluation attempts. */
export const dodRetrySchema = z.object({
  attempt: z.number(),
  prevAttempt: z.number().nullable(),
  fixed: z.array(z.string()),
  stillFailing: z.array(z.string()),
  newlyFailing: z.array(z.string()),
  pass: z.boolean().nullable(),
  at: z.string(),
});
export type DodRetry = z.infer<typeof dodRetrySchema>;

/** The deriver's recorded classification of this task's diff. */
export const dodClassificationSchema = z.object({
  classes: z.array(z.string()),
  /** paths → class, as counts (the ops record never stores a per-path dump). */
  byClass: z.record(z.number()),
  paths: z.number(),
  /** How many paths NO rule claimed — the residue. */
  unclassified: z.number(),
  unclassifiedSample: z.array(z.string()),
  /** Which version of the profile registry decided this. */
  registrySha: z.string(),
  at: z.string(),
  /** True when the FLOOR fired: nothing matched, so the minimum was minted. */
  floorApplied: z.boolean(),
});
export type DodClassification = z.infer<typeof dodClassificationSchema>;

/** The human's standing label on that classification, if one exists. */
export const dodLabelSchema = z.object({
  verdict: z.enum(["confirmed", "corrected"]),
  classes: z.array(z.string()),
  at: z.string(),
  by: z.string().nullable(),
});
export type DodLabel = z.infer<typeof dodLabelSchema>;

/** Per-class rollup over the whole ledger — where a correction becomes visible. */
export const dodClassMetricSchema = z.object({
  cls: z.string(),
  /** Paths the deriver assigned to this class, summed over `dod:classified`. */
  derivedPaths: z.number(),
  /** Labels asserting this class, split by whether the human agreed. */
  confirmed: z.number(),
  corrected: z.number(),
});
export type DodClassMetric = z.infer<typeof dodClassMetricSchema>;

/**
 * TRI-STATE, NOT FAIL-OPEN. `readable:false` means the read itself failed —
 * rendered as "treat this as UNKNOWN", never as an empty DoD. "0 criteria" and
 * "we could not look" are opposite facts, and a panel that draws them the same
 * way is the `vault.clear === null` fail-open shape one refactor away.
 */
export const taskDodSchema = z.object({
  readable: z.boolean(),
  unreadableReason: z.string().nullable(),
  taskUid: z.string().nullable(),
  criteria: z.array(dodCriterionSchema),
  bindingCount: z.number(),
  retries: z.array(dodRetrySchema),
  classification: dodClassificationSchema.nullable(),
  label: dodLabelSchema.nullable(),
  classMetrics: z.array(dodClassMetricSchema),
  /** The vocabulary a correction may pick from. */
  workClasses: z.array(z.string()),
});
export type TaskDod = z.infer<typeof taskDodSchema>;

const idParams = z.object({ id: z.coerce.number().int().positive() });

const taskDodOp: Operation<{ id: number }, void, void, TaskDod> = {
  method: "GET",
  path: ({ id }) => `/projects/api/tasks/${id}/dod`,
  params: idParams,
  response: taskDodSchema,
};

/** Confirm or correct the derived work-class. ONE tap = one labeled case. */
export const dodLabelBodySchema = z.object({
  verdict: z.enum(["confirmed", "corrected"]),
  /** The classes the human asserts are correct. Must be non-empty. */
  classes: z.array(workClassSchema).min(1),
});
export type DodLabelBody = z.infer<typeof dodLabelBodySchema>;

export const dodLabelResultSchema = z.object({
  ok: z.boolean(),
  /** Why the write was refused, when `ok` is false. */
  reason: z.string().nullable(),
  /**
   * True when this exact label already existed: the ops fingerprint matched, so
   * neither a second ops row nor a second eval case was written. Clicking
   * "Confirm" twice must not inflate the calibration corpus.
   */
  deduped: z.boolean(),
  opsEventId: z.number().nullable(),
  evalCaseUid: z.string().nullable(),
  /** Server truth after the write — the panel adopts this, never its own guess. */
  view: taskDodSchema,
});
export type DodLabelResult = z.infer<typeof dodLabelResultSchema>;

const dodLabelOp: Operation<{ id: number }, void, DodLabelBody, DodLabelResult> = {
  method: "POST",
  path: ({ id }) => `/projects/api/tasks/${id}/dod/label`,
  params: idParams,
  body: dodLabelBodySchema,
  response: dodLabelResultSchema,
};

/**
 * A task that HAS a DoD packet. Keyed on `dod_criteria`, not on the task board
 * (#45493): the board is ordered by status then sort_key, so every packet on a
 * churned vault sits in the `done` tail and a caller paging the board reads
 * "this vault has no DoD" off an ordering artefact.
 */
export const dodSubjectSchema = z.object({
  taskId: z.number(),
  taskUid: z.string(),
  title: z.string(),
  status: z.string(),
  criteriaCount: z.number(),
  boundAt: z.string().nullable(),
});
export type DodSubject = z.infer<typeof dodSubjectSchema>;

export const dodSubjectsQuerySchema = z.object({
  /** How many packets to return, freshest binding first. Capped server-side. */
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type DodSubjectsQuery = z.infer<typeof dodSubjectsQuerySchema>;

export const dodSubjectsSchema = z.object({
  subjects: z.array(dodSubjectSchema),
  /** How many packets exist in scope, so a caller can tell "none" from "clipped". */
  total: z.number(),
});
export type DodSubjects = z.infer<typeof dodSubjectsSchema>;

const dodSubjectsOp: Operation<void, DodSubjectsQuery, void, DodSubjects> = {
  method: "GET",
  path: () => `/projects/api/dod/subjects`,
  query: dodSubjectsQuerySchema,
  response: dodSubjectsSchema,
};

export const dod = {
  task: taskDodOp,
  label: dodLabelOp,
  subjects: dodSubjectsOp,
};

// ---------------------------------------------------------------------------
// Wire ↔ reader parity, checked by the compiler
// ---------------------------------------------------------------------------
//
// The zod schemas above and the reader's return type
// (@excellent/core/dod's TaskDodView) are two representations of ONE payload.
// Nothing at runtime forces them to agree, and the failure mode of a drift is
// the quiet one: a field added to the reader and forgotten here is stripped by
// `createRoute`'s response parse, so the panel renders a blank where a proof
// string should be and no test fails.
//
// These assignments are the pin. Both directions, because each catches a
// different mistake: schema→view catches a field the wire declares that the
// reader never produces; view→schema catches a field the reader produces that
// the wire would silently drop.
type _WireMatchesReader = TaskDod extends TaskDodView ? true : never;
type _ReaderMatchesWire = TaskDodView extends TaskDod ? true : never;
type _LabelWireMatchesReader = DodLabelResult extends DodLabelResultView ? true : never;
type _LabelReaderMatchesWire = DodLabelResultView extends DodLabelResult ? true : never;
// Same pin for the subject feed (#45493). `boundAt` is what makes the feed
// freshest-first; a drift that dropped it would leave the sensor scanning in an
// arbitrary order again — the exact defect this feed was added to remove.
type _SubjectWireMatchesReader = DodSubject extends DodSubjectView ? true : never;
type _SubjectReaderMatchesWire = DodSubjectView extends DodSubject ? true : never;
const _parity: [
  _WireMatchesReader,
  _ReaderMatchesWire,
  _LabelWireMatchesReader,
  _LabelReaderMatchesWire,
  _SubjectWireMatchesReader,
  _SubjectReaderMatchesWire,
] = [true, true, true, true, true, true];
void _parity;
